import { useEffect, useRef } from 'react'

/** Convert HSL (h: 0-360, s/l: 0-1) to a #RRGGBB hex string. */
const hslToHex = (h: number, s: number, l: number): string => {
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

/**
 * Shared singleton that drives all registered RGB shapes from a single
 * setInterval. Each subscriber gets the same color at the same time.
 */
const rgbShapeRegistry = {
  subscribers: new Set<(color: string) => void>(),
  intervalId: null as ReturnType<typeof setInterval> | null,
  startTime: 0,

  subscribe(cb: (color: string) => void) {
    this.subscribers.add(cb)
    if (this.subscribers.size === 1) {
      this.startTime = Date.now()
      this.intervalId = setInterval(() => this.tick(), 100) // 10fps
    }
  },

  unsubscribe(cb: (color: string) => void) {
    this.subscribers.delete(cb)
    if (this.subscribers.size === 0 && this.intervalId !== null) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  },

  tick() {
    const elapsed = Date.now() - this.startTime
    const hue = ((elapsed / 6000) * 360) % 360
    const color = hslToHex(hue, 1, 0.5)
    this.subscribers.forEach((cb) => cb(color))
  },
}

/**
 * Directly drives a Google Maps Polyline and optional Polygon (buffer) with
 * a cycling RGB color. Bypasses React entirely — mutates the shapes via
 * setOptions on a shared 10fps interval.
 *
 * Does nothing if `colorValue` is not `'rgb'`.
 */
export const useRgbCycleShapes = (
  colorValue: string | undefined,
  polylineRef: React.RefObject<google.maps.Polyline | null>,
  bufferRef?: React.RefObject<google.maps.Polygon | null>,
) => {
  const isRgb = colorValue?.toLowerCase() === 'rgb'
  const cbRef = useRef<((color: string) => void) | null>(null)

  useEffect(() => {
    if (!isRgb) {
      cbRef.current = null
      return
    }

    const cb = (color: string) => {
      polylineRef.current?.setOptions({ strokeColor: color })
      bufferRef?.current?.setOptions({ fillColor: color + '4D' })
    }

    cbRef.current = cb
    rgbShapeRegistry.subscribe(cb)

    return () => {
      rgbShapeRegistry.unsubscribe(cb)
    }
  }, [isRgb])
}

/**
 * Returns the resolved display color for a linestring.
 * For 'rgb' values, returns a fallback static color (the actual animation
 * is handled by useRgbCycleShapes directly on map shapes).
 */
export const resolveLinestringColor = (
  color: string | undefined,
  fallback: string,
): string => {
  if (color?.toLowerCase() === 'rgb') return '#FF0000'
  return color || fallback
}
